import React from "react";
import styled, { keyframes } from "styled-components";

// Reusable shimmer placeholder. Use anywhere images or data are loading.
//
// Usage:
//   <Skeleton width="100%" height="200px" radius="12px" />
//   <ImageWithSkeleton src="..." alt="..." />

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const Skeleton = styled.div`
  width: ${({ $width }) => $width || "100%"};
  height: ${({ $height }) => $height || "100%"};
  border-radius: ${({ $radius }) => $radius || "0"};
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 0px,
    rgba(0, 0, 0, 0.08) 40px,
    rgba(0, 0, 0, 0.04) 80px
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: rgba(0, 0, 0, 0.05);
  }

  [data-theme="dark"] & {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.08) 0px,
      rgba(255, 255, 255, 0.16) 40px,
      rgba(255, 255, 255, 0.08) 80px
    );
    background-size: 800px 100%;
  }
`;

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: inherit;
  background: rgba(0, 0, 0, 0.04);

  &[data-theme="dark"], [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
  }

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
    transition: opacity 400ms ease;
  }
`;

const SkeletonOverlay = styled(Skeleton)`
  position: absolute;
  inset: 0;
  opacity: ${({ $loaded }) => ($loaded ? 0 : 1)};
  pointer-events: none;
  transition: opacity 400ms ease;
`;

// Progressive image that shows a shimmer skeleton until the image decodes.
// Supports both <img> and <video> via `mediaType`.
export function ImageWithSkeleton({ src, alt, mediaType = "image", lazy = true, onReady }) {
  const [loaded, setLoaded] = React.useState(false);

  const handleLoad = React.useCallback(() => {
    setLoaded(true);
    if (onReady) onReady();
  }, [onReady]);

  return (
    <ImageWrap $loaded={loaded}>
      {mediaType === "video" ? (
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={handleLoad}
          preload={lazy ? "metadata" : "auto"}
        />
      ) : (
        <img
          src={src}
          alt={alt || ""}
          loading={lazy ? "lazy" : "eager"}
          decoding="async"
          onLoad={handleLoad}
          onError={handleLoad}
        />
      )}
      <SkeletonOverlay $loaded={loaded} aria-hidden="true" />
    </ImageWrap>
  );
}
